"use client";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { fetchAuthSession } from "@aws-amplify/auth";
import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import UniversityCard from "@packages/shared-components/leadform/university-card";
import Logo from "@packages/shared-components/user-profile/signin-signup/logo";
import { getCookie } from "@packages/lib/utlils/cookie-utils";
import { getCommonAPIHeaders, getProjectSpecificData, isPGS } from "@packages/lib/utlils/commonFunction";
import API_URL from "@packages/lib/server-actions/api-urls";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import {
  LeadFormSubmissionContext,
  LeadFormInteractionPayload,
} from "./types";
import { getInteractionType } from "./field-registry";
import { getCallbackUrl } from "@packages/lib/utlils/non-advert-pdf-utils";
import { triggerSigninToast } from "@packages/shared-components/common-utilities/toast/signin-toast";
import { logClickstreamEvent } from "@packages/lib/utlils/clickstream";
import {
  CS_EVENT_TYPES,
  CS_ACTION_TYPE,
  CS_NETWORK_ID,
  CS_SYSTEM_NAME,
  CS_FUNCTIONALITY,
  CS_CTA_TITLES,
  CS_PAGE_NAMES,
} from "@packages/lib/utlils/clickstream-constants";

const API_DOMAIN = process.env.NEXT_PUBLIC_BFF_API_DOMAIN || "";
const SITE_CODE = process.env.NEXT_PUBLIC_SITE_CODE || "WU";
const INTERACTIONS_ENDPOINT = "/hewebsites/v1/leadProfile/interactions/submit";

interface UserAddress {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
}

interface OneClickProspectusModalProps {
  isOpen: boolean;
  onClose: () => void;
  enquiryProps: any;
  prospectusType: "digital" | "physical";
  enquiryResponse: any;
  userDetails?: any;
}

const getAuthHeaders = (token?: string) => ({
  ...getCommonAPIHeaders(),
  siteCode: SITE_CODE,
  Lp: "Y",
  "x-correlation-id": uuidv4(),
  ...(token ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` } : {}),
});

const OneClickProspectusModal = ({
  isOpen,
  onClose,
  enquiryProps,
  prospectusType,
  enquiryResponse,
  userDetails,
}: OneClickProspectusModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // User profile data
  const [userAddress, setUserAddress] = useState<UserAddress | null>(null);

  // University data
  const [submissionContext, setSubmissionContext] = useState<LeadFormSubmissionContext>({
    collegeId: enquiryProps?.collegeId || "",
    courseId: enquiryProps?.courseId || "0",
  });

  // Reviews from overview API
  const [reviews, setReviews] = useState<any[]>([]);

  // Consent checkboxes
  const [stayInKnow, setStayInKnow] = useState(false);
  const [uniNewsletter, setUniNewsletter] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isPhysical = prospectusType === "physical";

  /** Fetch required data on mount — overview always, address only for physical */
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const idToken = isPGS() ? getCookie("pgsIdToken") : getCookie("wuIdToken");
      if (!idToken) {
        setFormError("Authentication required. Please sign in.");
        setIsLoading(false);
        return;
      }

      // Fetch institution overview (always needed for uni card)
      // + user profile details (address — only for physical prospectus)
      const overviewPromise = makeApiCall({
        url: `${API_DOMAIN}${API_URL.leadProfileInstitutionOverview}`,
        method: "GET",
        headers: {
          "x-api-key": process.env.NEXT_PUBLIC_X_API_KEY!,
          sitecode: String(process.env.SITE_CODE),
        },
        queryParams: {
          institutionId: enquiryProps?.collegeId,
          networkId: getProjectSpecificData("networkId"),
          affiliateId: String(getProjectSpecificData("affiliateId")),
        },
      });

      const [overviewData] = await Promise.all([overviewPromise]);
      console.log(overviewData,"overviewData")
      // Process institution overview
      if (overviewData) {
        const institution = overviewData?.institutionDetails;
        const reviewsData = overviewData?.reviews || [];
        setReviews(reviewsData);
        if (institution) {
          setSubmissionContext({
            collegeId: enquiryProps?.collegeId || "",
            courseId: enquiryProps?.courseId || "0",
            subOrderItemId: enquiryProps?.subOrderItemId || "",
            institutionId: institution.institutionId != null ? String(institution.institutionId) : undefined,
            institutionName: institution.institutionName || "",
            institutionNameDisplay: institution.institutionNameDisplay || enquiryProps?.collegeName || "",
            institutionLogo: institution.institutionLogo || "",
            institutionHeroImage: institution.institutionHeroImage || "",
            overallRating: institution.overallRating || 0,
            overallRatingExact: institution.overallRatingExact || undefined,
            reviewCount: institution.reviewCount || 0,
            institutionTextKey: institution.institutionTextKey || undefined,
            collegeName: institution.institutionNameDisplay || enquiryProps?.collegeName || "",
            orderItemId: enquiryResponse?.orderItemId || enquiryProps?.orderItemId,
            prospectusType,
            clientConsentFlag: enquiryResponse?.clientConsentFlag || institution.clientConsentFlag,
            clientConsentDate: institution.clientConsentDate ?? undefined,
            consentText: enquiryResponse?.consentText || institution.consentText,
          });
        }
      }

      // Process user profile (address) from userDetails prop passed from getEnquiryDetails
      if (userDetails) {
        setUserAddress({
          firstName: userDetails?.foreName || "",
          lastName: userDetails?.surname || "",
          addressLine1: userDetails?.address1 || "",
          addressLine2: userDetails?.address2 || "",
          city: userDetails?.town || "",
          postcode: userDetails?.postcode || "",
          country: userDetails?.country || "United Kingdom",
        });
      }
    } catch (error) {
      console.error("Failed to fetch one-click data:", error);
      setFormError("Unable to load form data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [enquiryProps, prospectusType, isPhysical, enquiryResponse, userDetails]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  /** Handle one-click submit */
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError("");

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session?.tokens?.idToken?.toString();
      if (!idToken) {
        setFormError("Unable to authenticate. Please try again.");
        return;
      }

      const affiliateId = Number(getProjectSpecificData("affiliateId")) || null;
      const widgetId = Number(getCookie("widgetId")) || null;

      const payload: LeadFormInteractionPayload = {
        interactionType: getInteractionType(isPhysical ? "physicalprospectus" : "digitalprospectus"),
        interactionId: null,
        collegeId: Number(submissionContext.collegeId) || 0,
        courseId: submissionContext.courseId ? Number(submissionContext.courseId) : null,
        interactivityId: null,
        dateSent: null,
        affiliateId,
        emailContent: null,
        providerEmailId: null,
        subOrderItemId: submissionContext.subOrderItemId ? Number(submissionContext.subOrderItemId) : null,
        profileId: null,
        widgetId,
        postEnquiryFlag: null,
        mobileFlag: null,
        appFlag: null,
        clientConsentFlag: stayInKnow ? "Y" : null,
        clientConsentDate: submissionContext.clientConsentDate || null,
        orderItemId: submissionContext.orderItemId ?? null,
        ipAddress: null,
        odEventId: null,
        qualificationInterest: null,
        subjectInterest: null,
        enquiryTypeId: null,
        trackUserActionId: null,
        createdAt: null,
        updatedAt: null,
        city: userAddress?.city || null,
        town: userAddress?.city || null,
        postCode: userAddress?.postcode || null,
        addressLineOne: userAddress?.addressLine1 || null,
        addressLineTwo: userAddress?.addressLine2 || null,
        country: userAddress?.country?.toLowerCase().replace(/\s+/g, "-") || null,
        requestUrl: typeof window !== "undefined" ? window.location.href : null,
        referrerUrl: typeof window !== "undefined" ? (document.referrer || getCallbackUrl() || "") : null,
        oneClickCheckboxFlag: dontShowAgain ? "Y" : null,
        firstName: userAddress?.firstName || null,
        lastName: userAddress?.lastName || null,
        userEmail: userDetails?.email || null,
        inTakeYear: userDetails?.yearOfEntry || null,
      };

      // Fallback: decode JWT for user fields if not available from userDetails
      const decoded: any = idToken ? jwtDecode(idToken) : null;
      if (!payload.firstName && decoded?.given_name) {
        payload.firstName = decoded.given_name;
      }
      if (!payload.lastName && decoded?.family_name) {
        payload.lastName = decoded.family_name;
      }
      if (!payload.userEmail && decoded?.email) {
        payload.userEmail = decoded.email.toLowerCase();
      }

      const response = await fetch(`${API_DOMAIN}${INTERACTIONS_ENDPOINT}`, {
        method: "POST",
        headers: getAuthHeaders(idToken),
        body: JSON.stringify(payload),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok || responseData?.status === 409) {
        if (response.status === 409 || responseData?.status === 409) {
          setFormError("You've already requested a prospectus for this university in the last 24 hours. Please try again later.");
          return;
        }
        throw new Error(`Submission failed: ${response.status}`);
      }

      // Clickstream: Java parity — fire ProspectusRequested/DigitalProspectusRequested on one-click submit
      // Java: GetProspectusFormController.oneclickSubmitMethod() → clickStreamForEnq() → clickStreamCall()
      logClickstreamEvent({
        eventType: isPhysical ? CS_EVENT_TYPES.PROSPECTUS_REQUESTED : CS_EVENT_TYPES.DIGITAL_PROSPECTUS_REQUESTED,
        pageName: CS_PAGE_NAMES.ENQUIRY_PAGE,
        actionType: CS_ACTION_TYPE,
        CTATitle: CS_CTA_TITLES.GET_PROSPECTUS,
        isAdvertiser: submissionContext.subOrderItemId ? "Y" : "N",
        idpcInstitutionId: submissionContext.collegeId || "",
        subOrderItemId: submissionContext.subOrderItemId || "",
        sourceOrderItemIdForEnq: enquiryProps?.sponsoredOrderItemId || "",
        networkId: CS_NETWORK_ID,
        idpcCourseId: submissionContext.courseId || "",
        systemname: CS_SYSTEM_NAME,
        surveyConsent: stayInKnow ? "Y" : "N",
        newsletterConsent: stayInKnow ? "Y" : "N",
        solusConsent: stayInKnow ? "Y" : "N",
        ...(isPhysical ? {} : {
          functionalityName: CS_FUNCTIONALITY.DOWNLOAD_PROSPECTUS,
          downloadFlag: "Yes",
        }),
      });

      triggerSigninToast("Your prospectus request has been sent");
      onClose();
    } catch (error: any) {
      setFormError(error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Navigate to full form page (Edit flow) — go directly to step 2 (address) */
  const handleEditAddress = () => {
    const baseUrl = `/degrees/prospectus${enquiryProps?.collegeName ? `/${enquiryProps.collegeName}-prospectus` : ""}/${enquiryProps?.collegeId}/${enquiryProps?.courseId || "0"}/${enquiryProps?.selectedSubject || "0"}/n-${enquiryProps?.subOrderItemId || ""}/order-prospectus.html?step=2`;
    window.location.href = baseUrl;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-shadow-white bg-white">
      <div className="shadow-custom-6 w-full md:w-[798px] md:mx-[16px] bg-white md:rounded-[8px] overflow-y-auto md:overflow-x-hidden md:custom-scrollbar-3 max-h-[100vh] md:max-h-[90vh] flex flex-col md:flex-row">
        {/* Left side - University Card */}
        <div className="w-full h-full md:w-[375px] shrink-0 order-2 bg-grey-50 md:order-1 md:border-r md:border-grey-200">
          <UniversityCard
            logoSrc={submissionContext.institutionLogo?.startsWith("http") ? submissionContext.institutionLogo : `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${submissionContext.institutionLogo || ""}`}
            name={submissionContext.institutionNameDisplay || "University"}
            rating={String(submissionContext.overallRatingExact || submissionContext.overallRating || "0")}
            reviewCount={String(submissionContext.reviewCount || "0")}
            backgroundImage={submissionContext.institutionHeroImage?.startsWith("http") ? submissionContext.institutionHeroImage : `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${submissionContext.institutionHeroImage || ""}`}
            collegeId={submissionContext.collegeId || "0"}
            reviews={reviews}
            maxSlidesPerView={1}
            sliderClassName="leadform-1click-slider"
            noRoundedRight
            headingClassName="px-[16px]"
            autoplay
            hideReadMore
            mobileSpaceBetween={16}
          />
          <div className="flex items-center justify-center gap-[12px] p-[16px] w-full md:w-[240px] mx-auto">
            <Logo />
          </div>
        </div>

        {/* Right side - Order form */}
        <div className="flex-1 p-[56px_16px] md:p-[72px_40px] relative order-1 md:order-2">
          {/* Close button */}
          <button
            type="button"
            className="absolute top-[8px] right-[12px] cursor-pointer"
            onClick={onClose}
            aria-label="Close"
          >
            <Image
              src="/static/assets/icons/close_white_icon.svg"
              alt=""
              width={24}
              height={24}
              className="custom-filter-icon"
            />
          </button>

          {isLoading ? (
            <div className="flex flex-col gap-[24px] animate-pulse">
              <div className="h-[24px] bg-grey-100 rounded w-3/4 mx-auto"></div>
              <div className="h-[16px] bg-grey-100 rounded w-1/2 mx-auto"></div>
              <div className="h-[120px] bg-grey-100 rounded"></div>
              <div className="h-[80px] bg-grey-100 rounded"></div>
              <div className="h-[44px] bg-grey-100 rounded"></div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex flex-col gap-[8px] text-center mb-[24px]">
                <h1 className="text-heading6 md:text-heading5 font-farro font-bold">
                  {isPhysical ? "Get a printed prospectus from" : "Get a digital prospectus from"}
                </h1>
                <p className="text-small">{submissionContext.institutionNameDisplay || "University"}</p>
              </div>

              {/* Address card (physical only) */}
              {isPhysical && userAddress && (
                <div className="bg-grey-50 border border-grey-200 rounded-[8px] p-[16px] mb-[24px]">
                  <div className="flex flex-col gap-[4px]">
                    <p className="small font-semibold">Send prospectus to</p>
                    <p className="small">
                      <span className="font-semibold">Name</span>: {userAddress.firstName} {userAddress.lastName}
                    </p>
                    <p className="small font-semibold">Address:</p>
                    {userAddress.addressLine1 && <p className="small">{userAddress.addressLine1}</p>}
                    {userAddress.addressLine2 && <p className="small">{userAddress.addressLine2}</p>}
                    {userAddress.city && <p className="small">{userAddress.city}</p>}
                    {userAddress.country && <p className="small">{userAddress.country}</p>}
                    {userAddress.postcode && <p className="small">{userAddress.postcode}</p>}
                    <button
                      type="button"
                      className="small font-semibold text-primary-400 text-left cursor-pointer mt-[4px]"
                      onClick={handleEditAddress}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}

              {/* Consent checkboxes */}
              <div className="space-y-[8px] mb-[24px]">
                <label className="small font-semibold">Stay in the know</label>
                <label className="flex items-start gap-[12px] cursor-pointer">
                  <input type="checkbox" className="peer hidden" checked={stayInKnow} onChange={(e) => setStayInKnow(e.target.checked)} />
                  <div className="w-[16px] h-[16px] mt-[1px] border border-grey-400 rounded flex items-center justify-center peer-checked:bg-blue-400 peer-checked:border-blue-400 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 flex-shrink-0">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition">
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.2534 0.723569C9.40607 0.863517 9.41638 1.10073 9.27643 1.2534L3.77643 7.2534C3.70732 7.3288 3.6104 7.37269 3.50815 7.37491C3.40589 7.37714 3.30716 7.33749 3.23483 7.26517L0.734835 4.76517C0.588388 4.61872 0.588388 4.38128 0.734835 4.23484C0.881282 4.08839 1.11872 4.08839 1.26517 4.23484L3.48822 6.45789L8.72357 0.746605C8.86351 0.593936 9.10073 0.583622 9.2534 0.723569Z" fill="white" stroke="white" strokeWidth="0.666667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="x-small text-grey-600">We&apos;ll send helpful updates and occasionally invite you to share your views.</span>
                </label>
                {submissionContext?.clientConsentFlag === "Y" && submissionContext?.consentText && (
                <label className="flex items-start gap-[12px] cursor-pointer">
                  <input type="checkbox" className="peer hidden" checked={uniNewsletter} onChange={(e) => setUniNewsletter(e.target.checked)} />
                  <div className="w-[16px] h-[16px] mt-[1px] border border-grey-400 rounded flex items-center justify-center peer-checked:bg-blue-400 peer-checked:border-blue-400 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 flex-shrink-0">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition">
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.2534 0.723569C9.40607 0.863517 9.41638 1.10073 9.27643 1.2534L3.77643 7.2534C3.70732 7.3288 3.6104 7.37269 3.50815 7.37491C3.40589 7.37714 3.30716 7.33749 3.23483 7.26517L0.734835 4.76517C0.588388 4.61872 0.588388 4.38128 0.734835 4.23484C0.881282 4.08839 1.11872 4.08839 1.26517 4.23484L3.48822 6.45789L8.72357 0.746605C8.86351 0.593936 9.10073 0.583622 9.2534 0.723569Z" fill="white" stroke="white" strokeWidth="0.666667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="x-small text-grey-600" dangerouslySetInnerHTML={{ __html: submissionContext.consentText }} />
                </label>
                )}
                <label className="flex items-start gap-[12px] cursor-pointer">
                  <input type="checkbox" className="peer hidden" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
                  <div className="w-[16px] h-[16px] mt-[1px] border border-grey-400 rounded flex items-center justify-center peer-checked:bg-blue-400 peer-checked:border-blue-400 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 flex-shrink-0">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition">
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.2534 0.723569C9.40607 0.863517 9.41638 1.10073 9.27643 1.2534L3.77643 7.2534C3.70732 7.3288 3.6104 7.37269 3.50815 7.37491C3.40589 7.37714 3.30716 7.33749 3.23483 7.26517L0.734835 4.76517C0.588388 4.61872 0.588388 4.38128 0.734835 4.23484C0.881282 4.08839 1.11872 4.08839 1.26517 4.23484L3.48822 6.45789L8.72357 0.746605C8.86351 0.593936 9.10073 0.583622 9.2534 0.723569Z" fill="white" stroke="white" strokeWidth="0.666667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="x-small text-grey-600">
                    Don&apos;t show this again, I&apos;m happy for this data to be sent each time I order a prospectus.
                  </span>
                </label>
              </div>

              {/* Error message */}
              {formError && (
                <p className="x-small text-negative-default text-center mb-[16px]" role="alert">
                  {formError}
                </p>
              )}

              {/* Submit Button */}
              <button
                type="button"
                className="btn btn-primary btn-medium w-full flex items-center justify-center gap-[8px] disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 12C4.5 7.875 7.875 4.5 12 4.5C16.125 4.5 19.5 7.875 19.5 12H22C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22V19.5C7.875 19.5 4.5 16.125 4.5 12Z" fill="white" />
                  </svg>
                ) : (
                  <>
                    {isPhysical ? "Order my free prospectus" : "Request digital prospectus"}
                    <Image src="/static/assets/icons/right_white_arrow.svg" width={17} height={14} alt="arrow icon" />
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OneClickProspectusModal;
